/** Console per-user preferences (namespace = console).
 *
 * A small typed layer over `/user_preferences/`. The whole set is fetched once
 * and cached (react-query); individual keys read from that cache. `useUserPreference`
 * is the generic accessor; the focused helpers are `useGridDefaultPreset`
 * (`grid_default:<grid>` → preset id, the grid-presets default view) and
 * `useGridCountMode` (`grid_count_mode:<grid>` → 'sync'|'async'|'deferred', the
 * count-mode override — localStorage-mirrored, see its own doc comment).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import type { CountMode } from '@/api/countStatus';
import * as userPrefsApi from '@/api/userPreferences';
import { useOptionalAuth } from '@/auth/AuthProvider';

const PREFS_KEY = ['userPreferences'] as const;

function usePrefsQuery() {
  const auth = useOptionalAuth();
  return useQuery({
    queryKey: PREFS_KEY,
    queryFn: () => userPrefsApi.list(),
    enabled: auth?.user != null,
    staleTime: 5 * 60_000,
  });
}

export interface UserPreferenceHandle<T> {
  value: T | null;
  isLoading: boolean;
  setValue: (v: T) => void;
  clear: () => void;
}

/** Generic accessor for one preference key. */
export function useUserPreference<T = unknown>(key: string): UserPreferenceHandle<T> {
  const qc = useQueryClient();
  const q = usePrefsQuery();
  const raw = q.data?.results.find((p) => p.key === key)?.value;

  const invalidate = () => void qc.invalidateQueries({ queryKey: PREFS_KEY });
  const setMutation = useMutation({
    mutationFn: (v: T) => userPrefsApi.save({ [key]: v }),
    onSuccess: invalidate,
  });
  const clearMutation = useMutation({
    mutationFn: () => userPrefsApi.remove([key]),
    onSuccess: invalidate,
  });

  return {
    value: (raw as T | undefined) ?? null,
    isLoading: q.isLoading,
    setValue: useCallback((v: T) => setMutation.mutate(v), [setMutation]),
    clear: useCallback(() => clearMutation.mutate(), [clearMutation]),
  };
}

/** Per-grid count-mode override (`grid_count_mode:<grid>`), mirrored to
 *  localStorage so it is readable on first paint.
 *
 *  The mirror is load-bearing, not an optimisation. The *default* mode lives on
 *  the backend view precisely so a cold first load never issues a COUNT — but an
 *  override has to be known before the first request goes out, and the
 *  preferences query resolves asynchronously. Without a synchronous read the
 *  override would apply only from the second request onward, i.e. every grid
 *  load would fire twice. The server preference stays the source of truth and
 *  wins on reconciliation (so a change made on another device propagates).
 */
export interface GridCountModeHandle {
  /** The user's override, or null to let the backend's own default stand. */
  mode: CountMode | null;
  setMode: (mode: CountMode) => void;
  clearMode: () => void;
}

const COUNT_MODES: readonly CountMode[] = ['sync', 'async', 'deferred'];
const countModeKey = (grid: string) => `grid_count_mode:${grid}`;

function asCountMode(value: unknown): CountMode | null {
  return typeof value === 'string' && (COUNT_MODES as readonly string[]).includes(value)
    ? (value as CountMode)
    : null;
}

function readMirror(grid: string): CountMode | null {
  try {
    return asCountMode(localStorage.getItem(countModeKey(grid)));
  } catch {
    return null; // private mode / storage disabled — fall back to the server pref
  }
}

function writeMirror(grid: string, mode: CountMode | null): void {
  try {
    if (mode === null) localStorage.removeItem(countModeKey(grid));
    else localStorage.setItem(countModeKey(grid), mode);
  } catch {
    /* non-fatal: the server preference still persists the choice */
  }
}

export function useGridCountMode(grid: string): GridCountModeHandle {
  const pref = useUserPreference<string>(countModeKey(grid));
  const [mirrored, setMirrored] = useState<CountMode | null>(() => readMirror(grid));

  // Server value is authoritative once it arrives; keep the mirror in step so
  // the next cold load starts from the right mode.
  const serverMode = asCountMode(pref.value);
  useEffect(() => {
    if (pref.isLoading) return;
    if (serverMode !== mirrored) {
      writeMirror(grid, serverMode);
      setMirrored(serverMode);
    }
  }, [grid, serverMode, mirrored, pref.isLoading]);

  const setMode = useCallback(
    (mode: CountMode) => {
      writeMirror(grid, mode);
      setMirrored(mode);
      pref.setValue(mode);
    },
    [grid, pref],
  );

  const clearMode = useCallback(() => {
    writeMirror(grid, null);
    setMirrored(null);
    pref.clear();
  }, [grid, pref]);

  return { mode: mirrored, setMode, clearMode };
}

export interface GridDefaultPreset {
  defaultPresetId: number | null;
  isLoading: boolean;
  setDefault: (presetId: number) => void;
  clearDefault: () => void;
}

/** The saved default preset id for one grid (or null). */
export function useGridDefaultPreset(grid: string): GridDefaultPreset {
  const pref = useUserPreference<number | string>(`grid_default:${grid}`);
  const v = pref.value;
  const defaultPresetId =
    typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : null;
  return {
    defaultPresetId,
    isLoading: pref.isLoading,
    setDefault: pref.setValue,
    clearDefault: pref.clear,
  };
}
